import { useMemo } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import type { CustomerOrder } from '../customerOrdersApi'
import type { CustomerProfile } from '../types'
import { useShopI18n } from '../../i18n'
import { buildCustomerAreaNavItems, CustomerAreaLayoutShell, customerMotionTransition } from './customer-area'
import { StripeTrustBanner } from './preferences/StripeTrustBanner'
import { AssociatedAddressSection } from './preferences/AssociatedAddressSection'
import { BillingDataSection } from './preferences/BillingDataSection'
import { LastPaymentSection } from './preferences/LastPaymentSection'
import { useCustomerPreferencesController } from './preferences/useCustomerPreferencesController'

type CustomerPreferencesPageProps = {
  profile: CustomerProfile
  orders: CustomerOrder[]
  ordersLoading: boolean
  onUpdateProfile: (payload: {
    firstName?: string
    lastName?: string
    taxCode?: string
    phone?: string
    email?: string
  }) => Promise<boolean>
  onNavigate: (targetPath: string) => void
}

export const CustomerPreferencesPage = ({
  profile,
  orders,
  ordersLoading,
  onUpdateProfile,
  onNavigate,
}: CustomerPreferencesPageProps) => {
  const { language, locale, t, toLocalizedPath } = useShopI18n()
  const prefersReducedMotion = Boolean(useReducedMotion())

  const navItems = useMemo(
    () =>
      buildCustomerAreaNavItems({
        t,
        toLocalizedPath,
        language,
      }),
    [language, t, toLocalizedPath],
  )

  const {
    billingFormState,
    setBillingFormState,
    useForNextOrders,
    setUseForNextOrders,
    billingError,
    saving,
    saveState,
    touchedTaxCode,
    setTouchedTaxCode,
    touchedBillingEmail,
    setTouchedBillingEmail,
    taxCodeInvalid,
    billingEmailInvalid,
    associatedAddress,
    associatedName,
    latestPaymentOrder,
    latestPaymentDate,
    saveBillingData,
  } = useCustomerPreferencesController({
    profile,
    orders,
    locale,
    t,
    onUpdateProfile,
  })

  const inputClassName = 'ca-input h-9 px-3 text-sm'
  const surfaceCardClassName = 'px-5 py-4 shadow-none'
  const paymentMetaCardClassName =
    'ca-card-shell ca-theme-transition rounded-xl px-3.5 py-3'

  return (
    <CustomerAreaLayoutShell
      eyebrow={t('common.customerArea')}
      title={t('customer.preferences.title')}
      subtitle={t('customer.preferences.subtitle')}
      mainClassName="space-y-6"
      navItems={navItems}
      activeNavId="preferences"
      onNavigate={onNavigate}
    >
      <StripeTrustBanner
        reducedMotion={prefersReducedMotion}
        prefix={t('customer.preferences.banner.prefix')}
        brand={t('customer.preferences.banner.brand')}
        suffix={t('customer.preferences.banner.suffix')}
        tooltip={t('customer.preferences.banner.tooltip')}
      />

      <BillingDataSection
        billingFormState={billingFormState}
        setBillingFormState={setBillingFormState}
        useForNextOrders={useForNextOrders}
        setUseForNextOrders={setUseForNextOrders}
        touchedTaxCode={touchedTaxCode}
        setTouchedTaxCode={setTouchedTaxCode}
        touchedBillingEmail={touchedBillingEmail}
        setTouchedBillingEmail={setTouchedBillingEmail}
        taxCodeInvalid={taxCodeInvalid}
        billingEmailInvalid={billingEmailInvalid}
        billingError={billingError}
        saveState={saveState}
        saving={saving}
        reducedMotion={prefersReducedMotion}
        inputClassName={inputClassName}
        surfaceCardClassName={surfaceCardClassName}
        onSave={() => {
          void saveBillingData()
        }}
        t={t}
      />

      <AssociatedAddressSection
        associatedAddress={associatedAddress}
        associatedName={associatedName}
        reducedMotion={prefersReducedMotion}
        surfaceCardClassName={surfaceCardClassName}
        onNavigate={onNavigate}
        toLocalizedPath={toLocalizedPath}
        t={t}
      />

      <LastPaymentSection
        ordersLoading={ordersLoading}
        latestPaymentOrder={latestPaymentOrder}
        latestPaymentDate={latestPaymentDate}
        locale={locale}
        language={language}
        surfaceCardClassName={surfaceCardClassName}
        paymentMetaCardClassName={paymentMetaCardClassName}
        onNavigate={onNavigate}
        toLocalizedPath={toLocalizedPath}
        t={t}
      />

      <AnimatePresence>
        {saveState === 'saved' ? (
          <motion.div
            initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -8 }}
            transition={customerMotionTransition}
            className="ca-theme-transition fixed top-4 right-4 z-40 rounded-[10px] border border-(--color-status-success-border) bg-(--color-bg-card) px-3 py-2 text-sm text-(--color-status-success) shadow-(--shadow-sm)"
            role="status"
            aria-live="polite"
          >
            {t('customer.preferences.billing.toastSaved')}
          </motion.div>
        ) : null}
      </AnimatePresence>
    </CustomerAreaLayoutShell>
  )
}
